// The second sign-in step of administrators and researchers (lamp-server utils/MultiFactor.ts).
// After the password, the server emails a code; once it is entered the server issues a session
// token that every API request then carries in SESSION_HEADER, next to the Basic credentials
// lamp-core sends. lamp-core knows nothing of this: window.fetch is wrapped (once, in index.tsx)
// to add the header to requests for the signed-in server, and to notice when the server stops
// taking the session (it expired), so the app can sign out (MFA_REQUIRED_EVENT).
//
// The session lives in sessionStorage (this tab, like lamp-core's credentials); a remembered
// browser's token in localStorage, per server and login id.
import LAMP from "lamp-core"
import i18n from "./i18n"

export const SESSION_HEADER = "X-SensorBrain-Session"
export const MFA_REQUIRED_EVENT = "sensorbrain:mfa-required"
const SESSION_KEY = "sensorbrain.mfa.session"
// Kept when the login screen clears localStorage (helper.js clearLocalStorageItems).
export const REMEMBER_KEY = "sensorbrain.mfa.remember"

type StoredSession = { base: string; id: string; token: string; until: number }

/** What the server said to the password (POST /auth/sign-in). */
export type SignInStep =
  | { mfa: "not-required" | "unsupported"; type?: string }
  | { mfa: "code-required"; type: string }
  | { mfa: "remembered"; type: string }
  | { mfa: "code-sent"; type: string; email_hint: string; expires_in: number }

/** An error from the sign-in routes, carrying the server's error string (e.g. "403.mfa-code-invalid"). */
export class MfaError extends Error {}

let installed = false
let notified = false

function storage(kind: "session" | "local"): Storage | null {
  try {
    return kind === "session" ? window.sessionStorage : window.localStorage
  } catch {
    return null
  }
}

function readSession(): StoredSession | null {
  try {
    const session = JSON.parse(storage("session")?.getItem(SESSION_KEY) ?? "null")
    return session && session.until > Date.now() ? session : null
  } catch {
    return null
  }
}

function readRemembered(): { [key: string]: string } {
  try {
    return JSON.parse(storage("local")?.getItem(REMEMBER_KEY) ?? "{}") || {}
  } catch {
    return {}
  }
}

function writeRemembered(map: { [key: string]: string }): void {
  try {
    storage("local")?.setItem(REMEMBER_KEY, JSON.stringify(map))
  } catch {}
}

/** The API base address for a server, as lamp-core builds it (https://, or http:// in dev mode). */
export function apiBase(serverAddress: string): string {
  return (LAMP.Auth as any)._get_base_address(serverAddress)
}

function basicAuth(id: string, password: string): string {
  return `Basic ${btoa(unescape(encodeURIComponent(`${id}:${password}`)))}`
}

function storeGrant(base: string, id: string, grant: any): void {
  if (!grant?.session_token) return
  try {
    storage("session")?.setItem(
      SESSION_KEY,
      JSON.stringify({ base, id, token: grant.session_token, until: Date.now() + (grant.expires_in ?? 0) * 1000 })
    )
  } catch {}
  if (grant.remember_token) writeRemembered({ ...readRemembered(), [`${base}|${id}`]: grant.remember_token })
  notified = false
}

async function post(base: string, route: string, id: string, password: string, body: any): Promise<Response> {
  return await fetch(`${base}${route}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json", Authorization: basicAuth(id, password) },
    body: JSON.stringify(body),
  })
}

async function readJson(response: Response): Promise<any> {
  try {
    return await response.json()
  } catch {
    return {}
  }
}

/**
 * Step 1, the password. "unsupported" when the server predates the second step (it answers 404):
 * the sign-in then goes on as before. Throws MfaError with the server's error otherwise. With
 * sendCode false the server only says what the account needs ("code-required"), sending nothing.
 * The code email follows the screen's language (Korean for ko, English otherwise).
 */
export async function mfaSignIn(
  serverAddress: string,
  id: string,
  password: string,
  { sendCode = true }: { sendCode?: boolean } = {}
): Promise<SignInStep> {
  const base = apiBase(serverAddress)
  const remember_token = readRemembered()[`${base}|${id}`]
  const request = sendCode ? (remember_token ? { remember_token } : {}) : { send_code: false }
  const language = i18n.language || navigator.language
  const response = await post(base, "/auth/sign-in", id, password, { ...request, language })
  const body = await readJson(response)
  if (response.status === 404) return { mfa: "unsupported" }
  if (!response.ok) throw new MfaError(body?.error ?? `${response.status}`)
  if (body.mfa === "remembered") storeGrant(base, id, body)
  return body as SignInStep
}

/** Step 2, the emailed code. Stores the session (and the remembered browser, when asked). */
export async function mfaVerify(
  serverAddress: string,
  id: string,
  password: string,
  code: string,
  remember: boolean
): Promise<void> {
  const base = apiBase(serverAddress)
  const response = await post(base, "/auth/verify", id, password, { code, remember })
  const body = await readJson(response)
  if (!response.ok) throw new MfaError(body?.error ?? `${response.status}`)
  storeGrant(base, id, body)
}

/** Ends the session on the server (best effort) and forgets it here. */
export async function mfaSignOut(): Promise<void> {
  const session = readSession()
  clearMfaSession()
  if (!session) return
  try {
    await fetch(`${session.base}/auth/sign-out`, {
      method: "POST",
      headers: { "Content-Type": "application/json", [SESSION_HEADER]: session.token },
      body: "{}",
    })
  } catch {}
}

export function clearMfaSession(): void {
  try {
    storage("session")?.removeItem(SESSION_KEY)
  } catch {}
}

/** Whether the dashboard runs inside the SensorBrain app (its WebView provides the login bridge). */
export function isInNativeApp(): boolean {
  const w = window as any
  return !!w?.webkit?.messageHandlers?.login || typeof w?.login?.postMessage === "function"
}

/** Tells the app to sign out (as lamp-core's LOGOUT does for participants, see index.tsx). */
export function signOutNativeApp(): void {
  const w = window as any
  const detail = { deleteCache: true }
  w?.webkit?.messageHandlers?.logout?.postMessage?.(detail)
  w?.logout?.postMessage?.(JSON.stringify(detail))
}

/** The message for a sign-in-step error, or null when it is not one of them. */
export function mfaErrorMessage(error: string, t: (key: string, options?: any) => string): string | null {
  switch (error) {
    case "400.mfa-email-required":
      return t(
        "This account's login ID is not an email address, so no sign-in code can be sent. Please contact your administrator."
      )
    case "503.mfa-email-failed":
      return t("The sign-in code could not be sent. Please try again later or contact your administrator.")
    case "429.mfa-code-recently-sent":
      return t("A code was sent less than 30 seconds ago. Please wait a moment.")
    case "429.mfa-too-many-codes":
      return t("Too many sign-in codes were requested. Please try again in an hour.")
    case "403.mfa-code-invalid":
      return t("The code is not correct.")
    case "403.mfa-code-expired":
      return t("The code has expired or was entered wrongly too many times. Please request a new code.")
    default:
      return null
  }
}

function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === "string") return input
  if (input instanceof URL) return input.href
  return (input as Request).url
}

async function noteResponse(response: Response): Promise<void> {
  if (response?.status !== 403 || notified) return
  try {
    const body = JSON.parse(await response.clone().text())
    if (body?.error === "403.mfa-required") {
      notified = true
      clearMfaSession()
      window.dispatchEvent(new Event(MFA_REQUIRED_EVENT))
    }
  } catch {}
}

/**
 * Wraps window.fetch (once): requests to the signed-in server get the session header, and a
 * "403.mfa-required" answer (the session ended) raises MFA_REQUIRED_EVENT once.
 */
export function installMfaSession(): void {
  if (installed || typeof window === "undefined" || typeof window.fetch !== "function") return
  installed = true
  const originalFetch = window.fetch
  window.fetch = function (input: RequestInfo | URL, init?: RequestInit) {
    const session = readSession()
    if (session && requestUrl(input).startsWith(`${session.base}/`)) {
      const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined))
      if (!headers.has(SESSION_HEADER)) headers.set(SESSION_HEADER, session.token)
      init = { ...(init ?? {}), headers }
    }
    return originalFetch.call(window, input as any, init).then(async (response: Response) => {
      await noteResponse(response)
      return response
    })
  } as typeof window.fetch
}
